import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { TablePaginationConfig } from 'antd';

import {
  getTasks,
  getTicketTypes,
  putToggleTaskPin,
  getDiscountApprovers,
  putTaskReopen,
  putTaskClose,
  putTicket,
  putTaskDueDate,
  putTask,
  postVoipCall,
} from 'queries';
import { useTaskCount } from 'hooks/useTaskCount';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem, TaskTypes } from 'types';
import { useQueryParams } from './useQueryParams';
import styles from '../tickets.module.scss';

type SearchParams = {
  userId?: number;
  filterBy: string;
  searchValue: string;
  status: string;
  taskType: TaskTypes;
  ticketType?: number | null;
  page: number;
  pageSize: number;
};

export const useTickets = () => {
  const { getParam, setParams } = useQueryParams();
  const { refresh: refreshTaskCounter } = useTaskCount();
  const [tablePagination, setTablePagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 5,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '50', '100'],
    className: styles.tickets__pagination,
    showTotal: (total) => `Total ${total} item${total !== 1 ? 's' : ''}`,
  });
  const [tableSearchParams, setTableSearchParams] = useState<SearchParams>({
    filterBy: getParam('search_type') || 'customer_id',
    searchValue: getParam('search_value') || '',
    status: getParam('status') || '1',
    taskType: TaskTypes.Tickets,
    page: tablePagination.current || 1,
    pageSize: tablePagination.pageSize || 5,
  });

  const { data: ticketTypes } = useQuery(['ticket-types'], getTicketTypes, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { data: ticketAssignees } = useQuery(['ticket-assignee'], getDiscountApprovers, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const {
    data: tickets,
    isLoading: isTasksLoading,
    refetch: refreshTaskList,
  } = useQuery(['get-tasks', tableSearchParams], () => getTasks(tableSearchParams), {
    onSuccess: (data: JsonDataItem) => {
      setTablePagination((prev) => ({
        ...prev,
        current: data.meta?.current_page || 1,
        total: data.meta?.total || 0,
        pageSize: data.meta?.per_page || 5,
      }));
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Toggle pin
  const { mutate: toggleTaskPin } = useMutation(putToggleTaskPin, {
    onSuccess: (data: JsonDataItem) => {
      Toast.show({ description: `Task has been ${data.locked ? 'pinned' : 'unpinned'}`, type: Toast.Type.Success });
      refreshTaskList();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Reopen ticket
  const {
    mutate: reopenTicket,
    isLoading: isTicketReopening,
    isSuccess: isReopened,
  } = useMutation(putTaskReopen, {
    onSuccess: (_, variables) => {
      setParams({ status: '1' });
      fetchTickets();
      Toast.show({ description: `Ticket #${variables.taskId} has been reopened.`, type: Toast.Type.Success });
      Toast.show({ description: 'A new note has been added.', type: Toast.Type.Success });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Close ticket
  const { mutate: closeTicket, isLoading: isTicketClosing } = useMutation(putTaskClose, {
    onSuccess: (_, variables) => {
      fetchTickets();
      Toast.show({ description: 'A new note has been added.', type: Toast.Type.Success });
      Toast.show({ description: `Ticket #${variables.taskId} has been closed.`, type: Toast.Type.Success });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Update task/ticket
  const { mutate: updateTask, isLoading: isTaskUpdating } = useMutation(putTask, {
    onSuccess: (_, variables) => {
      fetchTickets();
      Toast.show({
        description: `Ticket #${variables.taskId} has been ${variables.assignedToId ? 'reassigned' : 'updated'}.`,
        type: Toast.Type.Success,
      });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: updateTicket, isLoading: isTicketUpdating } = useMutation(putTicket, {
    onSuccess: (_, variables) => {
      const { successMessage, ticketId } = variables;
      fetchTickets();
      Toast.show({
        description: successMessage || `Ticket #${ticketId} has been updated.`,
        type: Toast.Type.Success,
      });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: updateTicketDueDate, isLoading: isDueDateUpdating } = useMutation(putTaskDueDate, {
    onSuccess: (_, variables) => {
      fetchTickets();
      Toast.show({ description: `Ticket #${variables.taskId} has been updated.`, type: Toast.Type.Success });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // call customer
  const { mutate: callToCustomer, isLoading: isCalling } = useMutation(postVoipCall, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const fetchTickets = () => {
    refreshTaskCounter();
    refreshTaskList();
  };

  const updateSearchParams = (updates: Partial<SearchParams>) => {
    setTableSearchParams((prev) => {
      const newParams = { ...prev, ...updates, page: updates.page ?? 1 };
      if (updates.searchValue !== undefined) setParams({ search_value: updates.searchValue });
      if (updates.status !== undefined) setParams({ status: updates.status });
      return newParams;
    });
  };

  return {
    ticketTypes,
    ticketAssignees,
    tableSearchParams,
    updateSearchParams,
    tablePagination,
    setTablePagination,
    tickets,
    isLoading:
      isTasksLoading ||
      isTicketReopening ||
      isTicketClosing ||
      isTicketUpdating ||
      isDueDateUpdating ||
      isTaskUpdating ||
      isCalling,
    reopenTicket,
    isReopened,
    toggleTaskPin,
    updateTask,
    updateTicket,
    updateTicketDueDate,
    closeTicket,
    callToCustomer,
  };
};
